<template>
  <div>
    <action-menu-wrapper :items="options" teleport :offset="offset" />

    <confirm-modal
      v-if="showDeleteModal"
      :title="$t('SCORECARDS.DELETE_SC_TITLE')"
      :description="$t('SCORECARDS.DELETE_SC_DESCRIPTION')"
      :confirm-button="$t('COMMON.DELETE')"
      confirm-button-type="error"
      :close="() => (showDeleteModal = false)"
      @confirm="handleDelete"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import { Ability, PermissionSubject } from "@/enums/auth";
import {
  SCORECARD_STATUS_DRAFT,
  SCORECARD_TYPE_MANUAL,
} from "@/helpers/constants";
import { modelIsScorecardGroup } from "@/helpers/scorecards";
import { usePermissions } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";
import scorecardsApiService from "@/services/modules/scorecards";
import useScorecardsStore from "@/stores/scorecards";

import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import ActionMenuWrapper, {
  type MenuItem,
} from "@/components/ui/ActionMenuWrapperV2.vue";

import type { Offset } from "@/models/common";
import type { IScorecard, IScorecardGroup } from "@/models/scorecards";
import type { PropType } from "vue";

const props = defineProps({
  model: {
    type: Object as PropType<IScorecard | IScorecardGroup>,
  },
  teleport: {
    type: Boolean,
    default: true,
  },
  isGroup: {
    type: Boolean,
    default: false,
  },
  value: [String, Number],
  modelValue: Object,
  offset: {
    type: Object as PropType<Offset>,
    default: () => ({ right: -40, top: 5 }),
  },
});

const { canPerformActionReactive } = usePermissions();

const router = useRouter();
const { t } = useI18n();
const { dispatch } = useStore();
const store = useScorecardsStore();

const { backtestReportScorecardId, activeBacktestPhase, viewParams } =
  storeToRefs(store);

const { fetchWrapper: handleDelete } = usePromiseWrapper(async () => {
  if (!props.model?.id) {
    return;
  }
  const action = props.isGroup
    ? scorecardsApiService.deleteGroup
    : scorecardsApiService.delete;
  const fetchAction = props.isGroup
    ? "scorecards/getScorecardGroups"
    : "scorecards/getAll";

  await action(props.model.id);
  await dispatch(fetchAction, viewParams.value);
});

const { fetchWrapper: handleDuplicate } = usePromiseWrapper(async () => {
  if (!props.model) {
    return;
  }
  const duplicateAction = props.isGroup
    ? "scorecards/createGroup"
    : "scorecards/duplicateScorecard";

  const fetchAction = props.isGroup
    ? "scorecards/getScorecardGroups"
    : "scorecards/getAll";

  const payload = modelIsScorecardGroup(props.model)
    ? {
        ...props.model,
        score_cards: props.model?.score_cards?.map((s) => s.id) || [],
        rules: props.model?.rules || [],
        name: props.model.name
          ? `${props.model.name} (${t("COMMON.COPY")})`
          : t("COMMON.UNNAMED_DUPLICATE"),
      }
    : props.model.id;

  await dispatch(duplicateAction, payload);
  await dispatch(fetchAction, viewParams.value);
});

const ACTIONS_MAP = {
  [t("COMMON.DUPLICATE")]: handleDuplicate,
  [t("COMMON.EDIT")]: handleEdit,
  [t("COMMON.DELETE")]: () => (showDeleteModal.value = true),
  [t("SCORECARDS.RUN_BT_REPORT")]: handleCreateBacktest,
};

const [canEditScoreCards, canDeleteScorecards] = [
  Ability.update,
  Ability.delete,
].map((action) =>
  canPerformActionReactive(
    props.isGroup
      ? PermissionSubject.scoreCardGroup
      : PermissionSubject.scoreCard,
    action,
    {
      [props.isGroup ? "scoreCardGroup" : "scoreCard"]: props.model?.id,
    }
  )
);

const canCreateScorecard = canPerformActionReactive(
  props.isGroup
    ? PermissionSubject.scoreCardGroup
    : PermissionSubject.scoreCard,
  Ability.create,
  {}
);

const options = computed(() => {
  const options: MenuItem[] = [];
  if (canEditScoreCards.value) {
    options.push({
      label: t("COMMON.EDIT"),
      handler: () => handleOption(t("COMMON.EDIT")),
    });
  }
  if (canCreateScorecard.value) {
    options.push({
      label: t("COMMON.DUPLICATE"),
      handler: () => handleOption(t("COMMON.DUPLICATE")),
    });
    options.push({
      label: t("SCORECARDS.RUN_BT_REPORT"),
      handler: () => handleOption(t("SCORECARDS.RUN_BT_REPORT")),
    });
  }
  if (canDeleteScorecards.value) {
    options.push({
      label: t("COMMON.DELETE"),
      handler: () => handleOption(t("COMMON.DELETE")),
    });
  }
  return options;
});
const showDeleteModal = ref(false);

const handleOption = (action: string) => {
  if (!props.model) {
    return;
  }
  ACTIONS_MAP[action]?.();
};

const editRoute = computed(() => {
  if (props.model?.type === SCORECARD_TYPE_MANUAL) {
    return "ScorecardsTemplate";
  }

  return props.model?.status === SCORECARD_STATUS_DRAFT
    ? "ScorecardsBuilder"
    : "ScorecardsOverview";
});

async function handleEdit() {
  const pushPayload = {
    name: props.isGroup ? "ScorecardsGroupEdit" : editRoute.value,
    params: {
      id: props.model?.id,
    },
  };

  router.push(pushPayload);
}

async function handleCreateBacktest() {
  if (!props.model?.id) {
    return;
  }

  backtestReportScorecardId.value = props.model.id;
  activeBacktestPhase.value = 1;
  router.push({
    name: "ScorecardsBacktestReportNew",
    query: {
      backTo: "Scorecards",
      type: props.isGroup ? "group" : "individual",
    },
  });
}
</script>
